import { Pan, Web3DViewer } from '@technology/web3d';
import { Web3dTool } from '@trimble/web3d-providers';
import { createEffect } from 'solid-js';
import { NavigationalCapabilityBase } from './base/navigational-capability-base';
import { getLogger } from '@trimble/logging';
import { ThreeJsManager } from '@trimble/web3d-utils';
import { NavigationTool } from '../helpers/web3d-tool-helper';

export const DEFAULT_CAMERA_HEIGHT = 2000;
const CAMERA_Z_LIMIT = 10;

export class MapNavigationCapability extends NavigationalCapabilityBase {
  protected override requiredWeb3DTools: NavigationTool[] = [
    Web3dTool.Pan,
    Web3dTool.Zoom,
  ];
  private originalNearMin: number | undefined = undefined;
  private originalMouseButton: number | undefined = undefined;
  private originalTouchCount: number | undefined = undefined;
  private readonly panTool: Pan = this.viewer.tools.get(Pan)!;
  protected logger = getLogger();

  constructor(
    viewer: Web3DViewer,
    private readonly threeJsManager?: ThreeJsManager
  ) {
    super(viewer);

    createEffect(() => {
      if (this.isActive()) {
        this.originalNearMin = this.viewer.camera.nearMin;
        this.originalMouseButton = this.panTool.mouseButton;
        this.originalTouchCount = this.panTool.touchCount;
        this.viewer.camera.nearMin = 0.0000001;
        this.panTool.mouseButton = 0;
        this.panTool.touchCount = 1;
        this.viewer.camera.quaternion.set(0, 0, 0, 1);
        this.addCameraLimitListener();
      } else {
        void this.cleanup();
      }
    });
  }

  /**
   * Enforces the camera Z position limit by checking and clamping
   * the camera position on each animation frame.
   * Zooming in too far on the map shows very blurry map tiles and
   * only one station. Also currently the different scaling values
   * for station mesh and path makes the path too broad when zoomed in.
   * Something to work on later, but not necessary for now if this limit applies.
   */
  private readonly enforceCameraLimit = (): void => {
    if (this.viewer.camera.position.z < CAMERA_Z_LIMIT) {
      this.viewer.camera.position.z = CAMERA_Z_LIMIT;
    }
  };

  private addCameraLimitListener(): void {
    if (this.threeJsManager) {
      this.threeJsManager.addAnimationFrameListener(this.enforceCameraLimit);
    }
  }

  private async removeCameraLimitListener(): Promise<void> {
    if (this.threeJsManager) {
      await this.threeJsManager.removeAnimationFrameListener(
        this.enforceCameraLimit
      );
    }
  }

  public override async cleanup(): Promise<void> {
    await this.removeCameraLimitListener();

    if (this.originalNearMin != null) {
      this.viewer.camera.nearMin = this.originalNearMin;
    }
    if (this.originalMouseButton != null) {
      this.panTool.mouseButton = this.originalMouseButton;
    }
    if (this.originalTouchCount != null) {
      this.panTool.touchCount = this.originalTouchCount;
    }

    return Promise.resolve();
  }
}
